/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import { IPriceComponentResources } from '@msdyn365-commerce/components';
import MilProductComponent from '../../../components/MilProductComponent';
import { IImageSettings } from '@msdyn365-commerce/core';
import { CommerceProperty, ProductSearchResult, ProductWarehouseInventoryInformation } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { getMultipleStockStatusAsync, getMultiUnitOfMeasureConversionAsync } from '../../../actions/DataActionExtension.g';
import { IGetMultiUnitOfMeasureConversionResponse } from '../../../actions/DataServiceEntities.g';
import { IMilSearchResultContainerResources } from '../mil-search-result-container.props.autogenerated';
import { ICustomerWarehouseEntity_V3 } from '../../../actions/DataServiceEntities.g';
import { displayedInventoryMethod } from '../../mil-quick-order/mil-quick-order.props.autogenerated';
import { getEstimatedAvailability } from '../../../utils/InventoryDisplayHelper';

interface IProductSearchResultItems {
    products: ProductSearchResult[];
    context: any;
    resources: IMilSearchResultContainerResources;
    imageSettings?: IImageSettings;
    moduleType: string;
    moduleId: string;
    allowBackNavigation?: boolean;
    telemetryContent: ITelemetryContent;
    quickviewSlot?: {} | null | undefined;
    productComparisonButton?: {} | null | undefined;
    channelInventoryConfigurationId?: number;
    isPriceMinMaxEnabled?: boolean;
    productsDimensionAvailabilities?: IProductsDimensionsAvailabilities[][];
    surchargeLabel: string;
    desiredNonstockCommerceName?: string;
    nonstockCommerceState?: string;
}

interface ICustInv {
    inventory: number;
}

/**
 * Returns the product inventory label.
 * @param  channelInventoryConfigurationId - The channel configuration Id.
 * @param  product - The product.
 * @returns The inventory label.
 */
export function getInventoryLabel(channelInventoryConfigurationId: number | undefined, product: ProductSearchResult): string | undefined {
    if (!channelInventoryConfigurationId || !ArrayExtensions.hasElements(product.AttributeValues)) {
        return undefined;
    }
    const inventoryAttribute = product.AttributeValues.find(attribute => attribute.RecordId === channelInventoryConfigurationId);
    if (inventoryAttribute) {
        return inventoryAttribute.TextValue;
    }
    return undefined;
}

export const ProductSearchResultItems: React.FC<IProductSearchResultItems> = ({
    products,
    context,
    imageSettings,
    resources,
    moduleType,
    moduleId,
    allowBackNavigation,
    telemetryContent,
    quickviewSlot,
    productComparisonButton,
    channelInventoryConfigurationId,
    isPriceMinMaxEnabled,
    productsDimensionAvailabilities,
    surchargeLabel,
    desiredNonstockCommerceName,
    nonstockCommerceState
}) => {
    const priceResources: IPriceComponentResources = {
        priceRangeSeparator: resources.priceRangeSeparator
    };

    const [uomConvData, setUomConvData] = React.useState<IGetMultiUnitOfMeasureConversionResponse | undefined>();
    const [warehouses, setWarehouses] = React.useState<ICustomerWarehouseEntity_V3[] | undefined>();
    const [estimatedAvailabilites, setEstimatedAvailabilites] = React.useState<ProductWarehouseInventoryInformation | undefined>();

    const inventoryMethod = context.app.config.displayedInventoryMethod;

    React.useEffect(() => {
        void getUomConversionData(products);

        const itemIds = Array.from(new Set(products.map((product: ProductSearchResult) => product.RecordId)));

        void getEstimatedAvailability(context, itemIds).then(({ estimatedAvailabilities, warehouses }) => {
            setEstimatedAvailabilites(estimatedAvailabilities);
            setWarehouses(warehouses);
        });
    }, [products]);

    const getUomConversionData = async (items: ProductSearchResult[]) => {
        const itemIds: number[] = [];
        items.map(item => {
            if (item.RecordId) {
                itemIds.push(item.RecordId);
            }
        });
        const stockStatuses = await getMultipleStockStatusAsync({ callerContext: context.actionContext }, itemIds);

        const resp: IGetMultiUnitOfMeasureConversionResponse = await getMultiUnitOfMeasureConversionAsync(
            { callerContext: context.actionContext },
            itemIds
        );
        if (resp && resp.UomConvMap && resp.UomConvMap.length > 0) {
            const tempRespUomData = [...resp.UomConvMap];
            items.map(item => {
                let index = -1;
                const uomData = tempRespUomData.find((res, i) => {
                    if (res.ItemRecId === item.RecordId) {
                        index = i;
                        return res;
                    }
                    return;
                });

                let stockDataIdx = -1;
                const stockStatusData = stockStatuses.StockStatusMap?.find((res, i) => {
                    if (res.ItemRecId === item.RecordId) {
                        stockDataIdx = i;
                        return res;
                    }
                    return;
                });

                if (index >= 0 && uomData) {
                    tempRespUomData.splice(index, 1);
                    const priceUnitProp: CommerceProperty = {
                        Key: 'uomPriceUnit',
                        Value: { DecimalValue: uomData.EcommPriceUnit || 1 }
                    };
                    const uomDescProp: CommerceProperty = {
                        Key: 'uomDesc',
                        Value: { StringValue: uomData.DefaultDescription }
                    };
                    const surchargeAmountProp: CommerceProperty = {
                        Key: 'surchargeAmount',
                        Value: { DecimalValue: uomData.EcommSurchargeAmount || 0 }
                    };
                    const surchargeFactorProp: CommerceProperty = {
                        Key: 'surchargeFactor',
                        Value: { DecimalValue: uomData.EcommSurchargeFactor || 0 }
                    };
                    const isSurchargePercentProp: CommerceProperty = {
                        Key: 'isSurchargePercent',
                        Value: { BooleanValue: uomData.IsEcommSurchargePercent || false }
                    };
                    const surchargeLabelProp: CommerceProperty = {
                        Key: 'surchargeLabel',
                        Value: { StringValue: surchargeLabel || 'Surcharge incl.' }
                    };
                    if (item.ExtensionProperties) {
                        item.ExtensionProperties.push(priceUnitProp);
                        item.ExtensionProperties.push(uomDescProp);
                        item.ExtensionProperties.push(surchargeAmountProp);
                        item.ExtensionProperties.push(surchargeFactorProp);
                        item.ExtensionProperties.push(isSurchargePercentProp);
                        item.ExtensionProperties.push(surchargeLabelProp);
                    }
                }

                if (stockDataIdx >= 0 && stockStatusData) {
                    const stockStatusProp: CommerceProperty = {
                        Key: 'stockStatus',
                        Value: { StringValue: stockStatusData.StockStatus }
                    };
                    if (item.ExtensionProperties) {
                        item.ExtensionProperties.push(stockStatusProp);
                    }
                }
            });
            setUomConvData(resp);
        }
    };

    return (
        <ul className='list-unstyled'>
            {!!uomConvData &&
                products.map((product: ProductSearchResult, index: number) => {
                    const productBehaviour = uomConvData.UomConvMap?.find(prod => prod.ItemRecId === product.RecordId);
                    const availInvs = estimatedAvailabilites?.ProductWarehouseInventoryAvailabilities?.filter(
                        inv => inv.ProductId === product.RecordId
                    );
                    const newCustInv: ICustInv[] = [];
                    if (warehouses && availInvs) {
                        warehouses.map(wh => {
                            const inventoryInfo = availInvs.find(inv => inv.InventLocationId === wh.AvailableWarehouse);
                            if (inventoryInfo) {
                                const newWhEntry = {
                                    inventory:
                                        inventoryMethod === displayedInventoryMethod.physicalAvailable
                                            ? inventoryInfo.PhysicalAvailable || 0
                                            : inventoryInfo.TotalAvailable || 0
                                };
                                newCustInv.push(newWhEntry);
                            }
                        });
                    }

                    const aggProductInvAvail = estimatedAvailabilites?.AggregatedProductInventoryAvailabilities?.find(
                        inv => inv.ProductId === product.RecordId
                    );
                    const totalInv =
                        warehouses && warehouses.length > 0 && newCustInv
                            ? newCustInv?.reduce((acc, curr) => acc + (curr.inventory >= 0 ? curr.inventory : 0), 0)
                            : aggProductInvAvail && inventoryMethod === displayedInventoryMethod.physicalAvailable
                            ? aggProductInvAvail?.PhysicalAvailableQuantity
                            : aggProductInvAvail?.TotalAvailableQuantity;
                    return (
                        <li className='ms-product-search-result__item' key={index}>
                            <MilProductComponent
                                context={context}
                                telemetryContent={telemetryContent}
                                imageSettings={imageSettings}
                                freePriceText={resources.priceFree}
                                originalPriceText={resources.originalPriceText}
                                currentPriceText={resources.currentPriceText}
                                shouldDisplayPrice={!!uomConvData}
                                ratingAriaLabel={resources.ratingAriaLabel}
                                allowBack={allowBackNavigation}
                                id={moduleId}
                                key={product.RecordId}
                                typeName={moduleType}
                                data={{ product }}
                                quickViewButton={quickviewSlot}
                                productComparisonButton={productComparisonButton}
                                inventoryLabel={getInventoryLabel(channelInventoryConfigurationId, product)}
                                isPriceMinMaxEnabled={isPriceMinMaxEnabled}
                                priceResources={priceResources}
                                swatchItemAriaLabel={resources.swatchItemAriaLabel}
                                dimensionAvailabilities={productsDimensionAvailabilities?.find(
                                    dimensionAvailability =>
                                        ArrayExtensions.hasElements(dimensionAvailability) &&
                                        dimensionAvailability[0].masterProductId === product.MasterProductId
                                )}
                                desiredNonstockCommerceName={desiredNonstockCommerceName || ''}
                                nonstockCommerceState={nonstockCommerceState || ''}
                                stockStatusText={resources.stockStatusText}
                                productBehavior={productBehaviour?.Product?.Behavior}
                                minimumOrderQtyLabelText={resources.minimumOrderQtyLabelText}
                                availableStockLabel={resources.availableStockLabel}
                                availInvs={totalInv || 0}
                                productUom={productBehaviour}
                                isEnabledProductDescription={true}
                            />
                        </li>
                    );
                })}
        </ul>
    );
};
